// src/Dashboard/pages/admin/Attendance/utils/attendanceUtils.js

export const STATUS_OPTIONS = [
  "",
  "Licencié",
  "Licencié Hors Club",
  "Inscrit",
  "Cours d'essai",
  "Licencié - Arrêt",
  "Non Licencié - Arrêt",
  "Ancien Licencié",
  "Ancien Licencié et Arrêt",
];

export const STOPPED_STATUSES = [
  "Licencié - Arrêt",
  "Non Licencié - Arrêt",
  "Ancien Licencié et Arrêt",
];

export function getLocalDateInput(date = new Date()) {
  const value = new Date(date);
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

export function normalizeAttendanceFilters(rawFilters = {}) {
  return {
    query: String(rawFilters.query || ""),
    selectedGroupId: String(rawFilters.selectedGroupId || ""),
    selectedDate: String(rawFilters.selectedDate || getLocalDateInput()),
    selectedSeason: String(rawFilters.selectedSeason || ""),
    statutInscription: String(rawFilters.statutInscription || ""),
    hideStopped: Boolean(rawFilters.hideStopped ?? true),
    onlyPresent: Boolean(rawFilters.onlyPresent ?? false),
    onlyAbsent: Boolean(rawFilters.onlyAbsent ?? false),
    sortByName: String(rawFilters.sortByName || "name_asc"),
  };
}

export function getSeasonNeighbors(season) {
  const match = /^(\d{4})\s*-\s*(\d{4})$/.exec(String(season || ""));

  if (!match) {
    return {
      prev: "",
      next: "",
    };
  }

  const start = Number(match[1]);
  const end = Number(match[2]);

  return {
    prev: `${start - 1}-${end - 1}`,
    next: `${start + 1}-${end + 1}`,
  };
}

export function buildDailyAttendanceMap({
  dateISO,
  groupId,
  adherentIds = [],
  attendances = [],
}) {
  const day = String(dateISO || "").slice(0, 10);
  const selectedGroupId = Number(groupId);
  const ids = new Set(adherentIds.map(Number));

  const map = {};

  adherentIds.forEach((id) => {
    map[id] = 0;
  });

  (attendances || []).forEach((attendance) => {
    const attendanceDay = String(attendance?.date_presence || "").slice(0, 10);
    const adherentId = Number(attendance?.adherent_id);
    const attendanceGroupId = Number(attendance?.group_id);

    if (
      attendanceDay === day &&
      attendanceGroupId === selectedGroupId &&
      ids.has(adherentId)
    ) {
      map[adherentId] = attendance?.presence ? 1 : 2;
    }
  });

  return map;
}

export function getAdherentName(adherent) {
  const nom = String(adherent?.nom || "")
    .trim()
    .toUpperCase();
  const prenom = String(adherent?.prenom || "").trim();

  return `${nom} ${prenom}`.trim() || "Adhérent";
}

export function getDefaultPhoto(genre) {
  const value = String(genre || "")
    .trim()
    .toLowerCase();

  const isFemale = ["f", "feminin", "féminin", "femme", "female"].includes(
    value,
  );

  return isFemale ? "/img/judokate_avatar.png" : "/img/judoka_avatar.png";
}

export function toAbsolute(url) {
  if (!url) return "";

  const value = String(url).trim();

  if (!value) return "";

  if (
    /^https?:\/\//i.test(value) ||
    value.startsWith("data:") ||
    value.startsWith("blob:")
  ) {
    return value;
  }

  const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";

  // Cas upload back : /uploads/photo.jpg ou uploads/photo.jpg
  if (value.startsWith("/uploads")) {
    return `${base}${value}`;
  }

  if (value.startsWith("uploads/")) {
    return `${base}/${value}`;
  }

  // Cas fichier public React : /img/judoka_avatar.png
  return value;
}

export function getAdherentPhoto(adherent) {
  const photo = toAbsolute(
    adherent?.photo_url ||
      adherent?.photoURL ||
      adherent?.photo ||
      adherent?.avatar,
  );

  if (photo) return photo;

  return getDefaultPhoto(adherent?.genre);
}

export function getGroupName(group) {
  return group?.nom || group?.name || `Groupe #${group?.id}`;
}

export function getGroupSubtitle(group) {
  return [group?.horaire1, group?.horaire2].filter(Boolean).join(" · ");
}

export function adherentBelongsToGroup(adherent, selectedGroup) {
  if (!selectedGroup?.id) return true;

  const selectedGroupId = Number(selectedGroup.id);

  // Nouvelle logique fiable
  if (Number(adherent?.group_id) === selectedGroupId) {
    return true;
  }

  // Fallback temporaire pour les anciennes données non migrées
  const selectedGroupName = String(
    selectedGroup?.nom || selectedGroup?.name || "",
  )
    .trim()
    .toLowerCase();

  const adherentGroupName = String(adherent?.adherent_groupe || "")
    .trim()
    .toLowerCase();

  return Boolean(selectedGroupName && adherentGroupName === selectedGroupName);
}

export function getAdherentGroupLabel(adherent, groups = []) {
  const group = groups.find(
    (item) => Number(item.id) === Number(adherent?.group_id),
  );

  return group?.nom || group?.name || adherent?.adherent_groupe || "";
}

export function hasStoppedStatus(adherent) {
  return STOPPED_STATUSES.includes(adherent?.statut_inscription);
}

export function sortAdherents(list = [], sortByName = "name_asc") {
  return list.slice().sort((a, b) => {
    const nameA = `${a?.nom || ""} ${a?.prenom || ""}`.trim();
    const nameB = `${b?.nom || ""} ${b?.prenom || ""}`.trim();

    const result = nameA.localeCompare(nameB, "fr", {
      sensitivity: "base",
    });

    return sortByName === "name_desc" ? -result : result;
  });
}

export function getAttendanceStatusMeta(status) {
  if (status === 1) {
    return {
      label: "Présent",
      shortLabel: "P",
      overlay: "bg-emerald-500/55",
      badge:
        "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-200 dark:ring-emerald-800/60",
      ring: "ring-emerald-300 dark:ring-emerald-700",
    };
  }

  if (status === 2) {
    return {
      label: "Absent",
      shortLabel: "A",
      overlay: "bg-rose-500/55",
      badge:
        "bg-rose-50 text-rose-700 ring-1 ring-rose-200 dark:bg-rose-900/20 dark:text-rose-200 dark:ring-rose-800/60",
      ring: "ring-rose-300 dark:ring-rose-700",
    };
  }

  return {
    label: "Non saisi",
    shortLabel: "—",
    overlay: "",
    badge:
      "bg-slate-100 text-slate-600 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700",
    ring: "ring-slate-200/70 dark:ring-slate-700/70",
  };
}

export function formatNumber(value) {
  return new Intl.NumberFormat("fr-FR").format(Number(value || 0));
}

export function daysArray(yyyyMM) {
  if (!yyyyMM) return [];

  const [year, month] = String(yyyyMM).split("-").map(Number);

  if (!year || !month) return [];

  const count = new Date(year, month, 0).getDate();

  return Array.from({ length: count }, (_, index) =>
    String(index + 1).padStart(2, "0"),
  );
}

export function getDefaultSeason() {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;

  // Saison sportive classique : septembre → juin
  // Janvier à août => saison commencée l'année précédente
  if (month <= 8) {
    return `${year - 1}-${year}`;
  }

  return `${year}-${year + 1}`;
}

export function normalizeSeason(season) {
  const raw = String(season || "").trim();

  // Accepte :
  // 2025-2026
  // 2025 - 2026
  // 2025/2026
  // 2025–2026
  const match = raw.match(/(\d{4})\D+(\d{4})/);

  if (!match) {
    return getDefaultSeason();
  }

  return `${match[1]}-${match[2]}`;
}

export function generateSeasonMonths(season) {
  const cleanSeason = normalizeSeason(season);
  const [startYear, endYear] = cleanSeason.split("-").map(Number);

  if (!startYear || !endYear) return [];

  const list = [];

  // Septembre à décembre
  for (let month = 9; month <= 12; month += 1) {
    const value = `${startYear}-${String(month).padStart(2, "0")}`;

    const label = new Date(startYear, month - 1, 1).toLocaleDateString(
      "fr-FR",
      {
        month: "long",
        year: "numeric",
      },
    );

    list.push({ value, label });
  }

  // Janvier à juin
  for (let month = 1; month <= 6; month += 1) {
    const value = `${endYear}-${String(month).padStart(2, "0")}`;

    const label = new Date(endYear, month - 1, 1).toLocaleDateString("fr-FR", {
      month: "long",
      year: "numeric",
    });

    list.push({ value, label });
  }

  return list;
}

export function getCurrentSeasonMonth(months = []) {
  const now = new Date();

  const current = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
    2,
    "0",
  )}`;

  return months.some((month) => month.value === current)
    ? current
    : months?.[0]?.value || "";
}

export function formatMonthShort(yyyyMM) {
  if (!yyyyMM) return "—";

  const [year, month] = String(yyyyMM).split("-").map(Number);

  if (!year || !month) return yyyyMM;

  return new Date(year, month - 1, 1).toLocaleDateString("fr-FR", {
    month: "short",
  });
}

export function formatMonthLabel(yyyyMM) {
  if (!yyyyMM) return "—";

  const [year, month] = String(yyyyMM).split("-").map(Number);

  if (!year || !month) return yyyyMM;

  return new Date(year, month - 1, 1).toLocaleDateString("fr-FR", {
    month: "long",
    year: "numeric",
  });
}

export function buildAttendanceRows({
  month,
  adherentsList = [],
  attendancesList = [],
  groupId,
}) {
  if (!month || !Array.isArray(adherentsList)) return [];

  const days = daysArray(month);
  const selectedGroupId = Number(groupId);

  const monthAttendances = (attendancesList || []).filter((attendance) => {
    const attendanceMonth = String(attendance?.date_presence || "").slice(0, 7);
    const attendanceGroupId = Number(attendance?.group_id);

    return attendanceMonth === month && attendanceGroupId === selectedGroupId;
  });

  const attendanceMap = {};

  monthAttendances.forEach((attendance) => {
    const adherentId = Number(attendance.adherent_id);
    const day = String(attendance.date_presence).slice(8, 10);

    if (!attendanceMap[adherentId]) {
      attendanceMap[adherentId] = {};
    }

    attendanceMap[adherentId][day] = Boolean(attendance.presence);
  });

  return adherentsList.map((adherent) => {
    let totalPresent = 0;
    let totalAbsent = 0;

    const dates = {};

    days.forEach((day) => {
      const value = attendanceMap[adherent.id]?.[day];

      if (value === true) totalPresent += 1;
      else if (value === false) totalAbsent += 1;

      dates[day] = value;
    });

    const totalMarked = totalPresent + totalAbsent;

    const rate =
      totalMarked > 0 ? Math.round((totalPresent / totalMarked) * 100) : 0;

    return {
      id: adherent.id,
      nom: adherent.nom || "",
      prenom: adherent.prenom || "",
      adherent,
      dates,
      totalPresent,
      totalAbsent,
      totalMarked,
      rate,
    };
  });
}

export function getCellMeta(value) {
  if (value === true) {
    return {
      label: "Présent",
      short: "P",
      className:
        "bg-emerald-500 text-white ring-emerald-500/30 hover:bg-emerald-600",
    };
  }

  if (value === false) {
    return {
      label: "Absent",
      short: "A",
      className: "bg-rose-500 text-white ring-rose-500/30 hover:bg-rose-600",
    };
  }

  return {
    label: "Non saisi",
    short: "",
    className:
      "bg-slate-100 text-slate-400 ring-slate-200 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-500 dark:ring-slate-700 dark:hover:bg-slate-700",
  };
}

export function buildMonthlyChartData({
  season,
  attendancesList = [],
  groupId,
}) {
  const months = generateSeasonMonths(season);
  const selectedGroupId = Number(groupId);

  const map = new Map(
    months.map((month) => [
      month.value,
      {
        month: month.value,
        label: formatMonthShort(month.value),
        present: 0,
        absent: 0,
      },
    ]),
  );

  (attendancesList || []).forEach((attendance) => {
    const month = String(attendance?.date_presence || "").slice(0, 7);
    const attendanceGroupId = Number(attendance?.group_id);

    if (!map.has(month)) return;
    if (attendanceGroupId !== selectedGroupId) return;

    if (attendance.presence === true) {
      map.get(month).present += 1;
    } else if (attendance.presence === false) {
      map.get(month).absent += 1;
    }
  });

  return Array.from(map.values()).map((item) => {
    const total = item.present + item.absent;
    const rate = total > 0 ? Math.round((item.present / total) * 100) : 0;

    return {
      ...item,
      total,
      rate,
    };
  });
}

export function getChartTotals(chartData = []) {
  const present = chartData.reduce((sum, item) => sum + item.present, 0);
  const absent = chartData.reduce((sum, item) => sum + item.absent, 0);
  const total = present + absent;
  const rate = total > 0 ? Math.round((present / total) * 100) : 0;

  return {
    present,
    absent,
    total,
    rate,
  };
}
